import FadeUp from "./FadeUp";
import { Check, ArrowRight } from "lucide-react";

const tiers = [
    {
        id: "growth",
        name: "Growth",
        price: "$4,000",
        cadence: "/ month",
        spend: "Up to $20k/month creator spend",
        features: [
            "Up to 6 channels out of YouTube, newsletters, podcasts, LinkedIn, Instagram, and TikTok",
            "30–40 collaborations per month",
            "360-degree execution",
            "Dedicated account manager",
            "Performance reporting",
            "Bi-weekly performance calls",
            "Full tech stack",
        ],
        recommended: true,
    },
    {
        id: "starter",
        name: "Starter",
        price: "$2,500",
        cadence: "/ month",
        spend: "Up to $10k/month creator spend",
        features: [
            "3 channels out of YouTube, newsletters, podcasts, LinkedIn, Instagram, and TikTok",
            "15–20 collaborations per month",
            "360-degree execution",
            "Dedicated account manager",
            "Performance reporting",
            "Bi-weekly performance calls",
            "Full tech stack",
        ],
        recommended: false,
    },
    {
        id: "trial",
        name: "Trial",
        price: "$1,500",
        cadence: "/ month",
        spend: "Up to $5k/month creator spend",
        features: [
            "YouTube only",
            "360-degree execution",
            "Dedicated account manager",
            "Performance reporting",
            "Bi-weekly performance calls",
            "Full tech stack",
        ],
        recommended: false,
    },
];

const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Pricing = () => (
    <section
        id="pricing"
        data-testid="pricing-section"
        className="py-24 md:py-32 bg-white"
    >
        <div className="container-page">
            <div className="max-w-3xl">
                <FadeUp>
                    <p className="text-xs font-bold tracking-[0.22em] uppercase text-gray-500">
                        Pricing
                    </p>
                </FadeUp>
                <FadeUp delay={0.05}>
                    <h2
                        data-testid="pricing-heading"
                        className="mt-4 font-display font-extrabold text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.3] md:leading-[1.3] lg:leading-[1.3] text-[#0A0A0A] text-balance"
                    >
                        Simple monthly pricing.
                    </h2>
                </FadeUp>
                <FadeUp delay={0.1}>
                    <p className="mt-5 text-[15.5px] md:text-base text-gray-600 leading-relaxed max-w-2xl">
                        Pick the plan that matches your creator budget. Every plan includes 360-degree execution, a dedicated account manager, and the full tech stack.
                    </p>
                </FadeUp>
            </div>

            <div data-testid="pricing-grid" className="mt-14 grid lg:grid-cols-3 gap-5 md:gap-6 items-stretch">
                {tiers.map((t, i) => (
                    <FadeUp key={t.id} delay={0.05 + i * 0.06}>
                        <div
                            data-testid={`pricing-card-${t.id}`}
                            className={`h-full rounded-3xl p-7 md:p-8 flex flex-col ${
                                t.recommended
                                    ? "bg-[#0A0A0A] text-white border border-[#0A0A0A]"
                                    : "bg-white border border-black/[0.08]"
                            }`}
                        >
                            <div
                                data-testid={`pricing-name-${t.id}`}
                                className={`text-[11px] font-bold tracking-[0.2em] uppercase ${
                                    t.recommended ? "text-white/60" : "text-gray-500"
                                }`}
                            >
                                {t.name}
                            </div>
                            <div className="mt-6 flex items-baseline gap-1.5">
                                <span
                                    data-testid={`pricing-price-${t.id}`}
                                    className={`font-display font-extrabold text-5xl md:text-[52px] tracking-[-0.03em] leading-none ${
                                        t.recommended ? "text-white" : "text-[#0A0A0A]"
                                    }`}
                                >
                                    {t.price}
                                </span>
                                <span
                                    className={`text-sm font-medium ${
                                        t.recommended ? "text-white/60" : "text-gray-500"
                                    }`}
                                >
                                    {t.cadence}
                                </span>
                            </div>

                            <div
                                data-testid={`pricing-spend-${t.id}`}
                                className={`mt-6 rounded-xl px-4 py-3 text-[13.5px] font-semibold leading-[1.5] ${
                                    t.recommended
                                        ? "bg-white/10 text-white"
                                        : "bg-[#FAFAFA] border border-black/[0.06] text-[#0A0A0A]"
                                }`}
                            >
                                {t.spend}
                            </div>

                            <ul className="mt-6 space-y-3 flex-1">
                                {t.features.map((f) => (
                                    <li key={f} className="flex items-start gap-3">
                                        <Check
                                            className={`h-4 w-4 mt-1 flex-none ${
                                                t.recommended ? "text-[#C6F432]" : "text-[#0A0A0A]"
                                            }`}
                                            strokeWidth={2.5}
                                        />
                                        <span
                                            className={`text-[14.5px] leading-[1.65] ${
                                                t.recommended ? "text-white/85" : "text-gray-700"
                                            }`}
                                        >
                                            {f}
                                        </span>
                                    </li>
                                ))}
                            </ul>

                            <button
                                data-testid={`pricing-cta-${t.id}`}
                                onClick={() => scrollTo("final-cta")}
                                className={`mt-10 w-full inline-flex items-center justify-center gap-2 rounded-full h-12 text-sm font-semibold transition-all hover:-translate-y-[1px] ${
                                    t.recommended
                                        ? "bg-white text-[#0A0A0A] hover:bg-[#C6F432]"
                                        : "bg-[#0A0A0A] hover:bg-black text-white"
                                }`}
                            >
                                Book a Strategy Call
                                <ArrowRight className="h-4 w-4" />
                            </button>
                        </div>
                    </FadeUp>
                ))}
            </div>
        </div>
    </section>
);

export default Pricing;
